import { useEffect, useState } from "react";

type UseMicrophoneVolumeReturn = {
  hasAudioTrack: boolean;
  volumeLevel: number; // between 0..1
};

/**
 * Observes a MediaStream and returns a display-ready volume level
 */
export const useMicrophoneVolume = (
  stream: MediaStream | null
): UseMicrophoneVolumeReturn => {
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [hasAudioTrack, setHasAudioTrack] = useState(false);

  useEffect(() => {
    // No observable stream right now. Drop the meter to zero but keep the last
    // known `hasAudioTrack` value so the indicator does not flash an error
    // state across transient null windows.
    if (!stream) {
      setVolumeLevel(0);
      return;
    }

    const audioTracks = stream.getAudioTracks();
    // Only treat the stream as usable if at least one audio track is both live and enabled.
    const hasTrack = audioTracks.some(
      (track) => track.readyState === "live" && track.enabled
    );

    setHasAudioTrack(hasTrack);

    if (!hasTrack) {
      setVolumeLevel(0);
      return;
    }

    const audioContextClass = window.AudioContext;

    // If Web Audio isn't available, we can't compute volume.
    if (!audioContextClass) {
      setVolumeLevel(0);
      return;
    }

    const audioContext = new audioContextClass();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(stream);
    let animationFrameId = 0;

    // Small FFT size is enough because we only need a rough level meter,
    // not detailed frequency analysis.
    analyser.fftSize = 256;

    // Smooth changes a bit so the meter doesn't flicker too aggressively.
    analyser.smoothingTimeConstant = 0.85;
    const sampleBuffer = new Uint8Array(analyser.fftSize);
    source.connect(analyser);

    const readLevel = () => {
      // Read the current audio waveform into a byte buffer.
      analyser.getByteTimeDomainData(sampleBuffer);

      // Calculate RMS
      let squareTotal = 0;
      for (let i = 0; i < sampleBuffer.length; i += 1) {
        const normalizedSample = (sampleBuffer[i]! - 128) / 128;
        squareTotal += normalizedSample * normalizedSample;
      }

      const rms = Math.sqrt(squareTotal / sampleBuffer.length);
      setVolumeLevel(mapRmsToLevel(rms));

      // Keep measuring once per animation frame while this hook is active.
      animationFrameId = window.requestAnimationFrame(readLevel);
    };

    readLevel();

    return () => {
      // Cleanup on unmount or when the stream changes.
      window.cancelAnimationFrame(animationFrameId);
      source.disconnect();
      analyser.disconnect();

      void audioContext.close();
    };
  }, [stream]);

  return { hasAudioTrack, volumeLevel };
};

// Minimum RMS value we treat as meaningful input.
// Anything below this is considered background noise / silence.
const NOISE_FLOOR_RMS = 0.01;

// RMS value at which the meter is treated as "full" for normal speech.
// Louder input is still possible, but the displayed level will clamp at 1.
const SPEECH_CEILING_RMS = 0.12;

// Shapes the meter response, so low-to-mid speech levels are easier to see.
// Values below 1 make the meter feel more responsive for quiet speech.
const LEVEL_CURVE_EXPONENT = 0.65;

/**
 * Convert a raw RMS signal level into a normalized UI meter value.
 *
 * RMS (root mean square): a common approximation of signal loudness.
 */
const mapRmsToLevel = (rms: number): number => {
  // Normalise rms to a value between 0 and 1
  const normalized =
    (rms - NOISE_FLOOR_RMS) / (SPEECH_CEILING_RMS - NOISE_FLOOR_RMS);

  // Clamp it so it never goes below 0 or above 1
  const clamped = Math.min(1, Math.max(0, normalized));

  // Apply a power curve to make the meter more visually useful
  return Math.pow(clamped, LEVEL_CURVE_EXPONENT);
};
